import { useRef } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useFocusTrap } from "@/lib/useFocusTrap";
import { useModalOverlay } from "@/lib/useModalOverlay";

// EP-19 / EP-15 — one picture, full size, over the page. The same shell as the
// EF-8 snapshot gallery's lightbox (focus trap, scroll lock, Escape, click
// outside), for a single image: a question's picture on the candidate and
// recruiter pages, a support screenshot in the back office. The `src` is always
// one of OUR routes — nothing here ever loads a cross-origin URL.
export function PictureLightbox({
  src,
  alt,
  title,
  onClose,
}: {
  src: string;
  alt: string;
  /** Shown in the header; defaults to the alt text. */
  title?: string;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useModalOverlay({ open: true, onEscape: onClose });
  useFocusTrap(containerRef, true);
  const heading = title ?? alt;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={heading || "Picture"}
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-4xl flex-col gap-3 rounded-lg border border-border bg-card p-3 shadow-lg sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 truncate text-sm text-muted-foreground">{heading}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-11 w-11 shrink-0 cursor-pointer p-0"
            onClick={onClose}
            aria-label="Close picture"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="max-h-[calc(100dvh-8rem)] w-full rounded-md bg-muted/40 object-contain"
        />
      </div>
    </div>
  );
}

export default PictureLightbox;
